import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlmCurtailmentPlan,
  Organization,
  useRemovePlmCurtailmentPlanMutation,
  usePlmCurtailmentPlanListQuery,
  usePlmCurtailmentPlanListUpdateSubscription,
  EnumPlmCurtailmentActionDeviceType,
} from '../../../../types/generated-types';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import { Notifier } from '../../../system/services/notificationManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useInjectableComponents } from '../../../system/services/injectableComponentsManager';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';

// Define a more specific type that matches the generated types
interface MyCurtailmentPlan
  extends Omit<
    Partial<PlmCurtailmentPlan>,
    'curtailmentActions' | 'organization'
  > {
  curtailmentActions?: Array<{
    _id: string;
    deviceType: EnumPlmCurtailmentActionDeviceType;
    estimatedReductionKW: number;
  }>;
  organization?: Pick<Organization, '_id' | 'name'> | null;
}

export const PlmCurtailmentPlanList: React.FC = () => {
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const {
    setContextMenuItems,
    setTitle,
    setSubtitle,
    setSubtitlePath,
    setTitlePath,
  } = useInjectableComponents();

  const { data, loading } = usePlmCurtailmentPlanListQuery({
    fetchPolicy: 'network-only',
  });

  usePlmCurtailmentPlanListUpdateSubscription({
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  const [removePlmCurtailmentPlan] = useRemovePlmCurtailmentPlanMutation();

  const columns: MRT_ColumnDef<MyCurtailmentPlan>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'organization.name',
      header: 'Organization',
    },
    {
      accessorKey: 'estimatedReductionKW',
      header: 'Estimated Reduction (kW)',
      size: 235,
      minSize: 235,
      maxSize: 250,
    },
    {
      accessorKey: 'applicableSeasons',
      header: 'Applicable Seasons',
      Cell: ({ row }) => row.original.applicableSeasons?.join(', ') ?? '',
    },
    {
      accessorKey: 'action',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          <Tooltip title="View">
            <IconButton onClick={() => handleView(row.original._id!)}>
              <Visibility />
            </IconButton>
          </Tooltip>
          {can('update', 'PlmCurtailmentPlan') && (
            <Tooltip title="Edit">
              <IconButton onClick={() => handleEdit(row.original._id!)}>
                <Edit />
              </IconButton>
            </Tooltip>
          )}
          {can('delete', 'PlmCurtailmentPlan') && (
            <Tooltip title="Delete">
              <IconButton onClick={() => handleDelete(row.original._id!)}>
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const handleView = (id: string) => {
    navigate(`/plmCurtailmentPlan/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/plmCurtailmentPlan/${id}/edit`);
  };

  const handleDelete = async (id: string) => {
    try {
      await removePlmCurtailmentPlan({
        variables: { _id: id },
      });
      Notifier.success('Curtailment plan deleted successfully');
    } catch (error) {
      Notifier.error('Failed to delete curtailment plan');
    }
  };

  React.useEffect(() => {
    setContextMenuItems([
      {
        items: [
          {
            id: 'add-curtailment-plan',
            icon: <AddCircleOutlineIcon />,
            label: 'Add Curtailment Plan',
            navTarget: '/plmCurtailmentPlan/new',
            permit: { action: 'create', subject: 'PlmCurtailmentPlan' },
            action: () => navigate('/plmCurtailmentPlan/new'),
          },
        ],
      },
    ]);
    return () => setContextMenuItems(undefined);
  }, [navigate, setContextMenuItems]);

  const isSmall = useIsSmallScreen();

  React.useEffect(() => {
    setTitle(
      isSmall ? 'Curtailment Plans' : 'Peak Load Management Curtailment Plans',
    );
    setTitlePath('/plmCurtailmentPlan');
    return () => {
      setTitle(undefined);
      setTitlePath(undefined);
    };
  }, [setTitle, setTitlePath, isSmall]);

  if (loading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <MaterialReactTable
        columns={columns}
        data={
          data?.plmCurtailmentPlans
            ?.filter((plan): plan is NonNullable<typeof plan> => plan !== null)
            .map((plan) => ({
              ...plan,
              curtailmentActions:
                plan.curtailmentActions?.filter(
                  (action): action is NonNullable<typeof action> =>
                    action !== null,
                ) ?? [],
            })) ?? []
        }
        defaultColumn={{ size: 100 }}
        state={{ isLoading: loading }}
        enableColumnFilters={false}
        enableColumnActions={false}
        enableColumnResizing={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableRowSelection={false}
        enableTopToolbar={false}
        muiTableBodyRowProps={{ hover: false }}
        muiTableHeadCellProps={{
          sx: {
            fontWeight: 'bold',
            fontSize: '14px',
            p: '0.5rem',
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '14px',
            p: '0.5rem',
          },
        }}
        layoutMode="semantic"
      />
    </Box>
  );
};
